import React, { useEffect } from 'react'
import { SortableItem } from './SortableItem'
import { useTranslation } from 'react-i18next'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import { Divider, Grid, IconButton, Stack, Typography } from '@mui/material'
import { DragIndicator, VisibilityOff } from '@mui/icons-material'
import { getDefaultSetup } from './Utils'
import { useTheme } from '@mui/material/styles'
import { SetupFormGroup } from './SetupFormGroup'
import { Summary } from './Summary'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { TrainingWorkoutGroupInterface } from '../../../models/interfaces/training-workout-group.interface'
import { Setup } from '../../../models/interfaces/setup.interface'
import { WorkoutTypeEnum } from '../../../models/enums/workout-type.enum'
import {
  reorderGroupTrainingWorkout,
  setReloadTrainingWorkout,
  showModalCombine
} from '../../../redux/reducers/training-workout/training-workout.actions'
import { selectedTrainingWorkoutType } from '../../../redux/reducers/training-workout/training-workout.selectors'
import Iconify from '../../Iconify'

export function SortableItemGroup ({ id, data, index, trainingId }: any) {
  const dispatch = useAppDispatch()
  const trainingWorkoutType = useAppSelector(selectedTrainingWorkoutType)
  const trainingWorkoutGroup = data as TrainingWorkoutGroupInterface
  const { t } = useTranslation()
  const theme = useTheme()
  const [isEditFormVisible, setIsEditFormVisible] = React.useState(false)
  const [setupGroup, setSetupGroup] = React.useState<Setup[]>([])
  const dataSorted = trainingWorkoutGroup?.trainingsWorkouts ?? []
  const toggleEditFormVisibility = () => setIsEditFormVisible(!isEditFormVisible)
  const toggleUpdateTypeGroupVisibility = (trainingWorkoutGroup: TrainingWorkoutGroupInterface) => {
    dispatch(showModalCombine({ groupTrainingWorkout: trainingWorkoutGroup }))
  }

  useEffect(() => {
    load()
  }, [data])

  const load = () => {
    if (trainingWorkoutGroup.setup.length > 0) {
      setSetupGroup(trainingWorkoutGroup.setup)
    } else {
      setSetupGroup(getDefaultSetup(WorkoutTypeEnum.BODYBUILDING))
    }
  }

  const handleDragEnd = async (event: any) => {
    if (event?.destination) {
      const startIndex = event.source.index
      const newSort = Number(event.destination.index) + 1
      await dispatch(reorderGroupTrainingWorkout({
        type: trainingWorkoutType,
        payload: {
          trainingWorkoutId: String(dataSorted[startIndex].id),
          newSort
        },
        trainingWorkoutGroupId: id
      }))
      dispatch(setReloadTrainingWorkout())
    }
  }

  const getListStyle = (isDraggingOver: boolean) => ({
    background: isDraggingOver ? theme.palette.grey[600] : 'none',
    padding: 5,
    paddingBottom: 5,
    paddingLeft: 10,
    paddingRight: 10
  })

  const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
    userSelect: 'black',
    background: isDragging ? theme.palette.primary.main : theme.palette.grey[800],
    marginTop: 8,
    borderRadius: 8,
    padding: 10,
    ...draggableStyle
  })

  return (
    <Draggable draggableId={trainingWorkoutGroup.id} index={index} key={trainingWorkoutGroup.id}>
      {(provided, snapshot) => (
        <Stack
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}>
          <Grid container spacing={2}>
            <Grid item xs={0.3}>
              <Stack height={'100%'} direction="column" justifyContent="center" alignItems="center">
                <DragIndicator/>
              </Stack>
            </Grid>
            <Grid item xs={11.7}>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId={dataSorted.length.toString()}>
                  {(provided, snapshot) => (
                    <Stack
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      style={getListStyle(snapshot.isDraggingOver)}>
                      {dataSorted.map((trainingWorkout, index) =>
                        <Stack key={index}>
                          <SortableItem isGrouped={true} data={trainingWorkout} index={index} trainingId={trainingId} />
                        </Stack>
                      )}
                      {provided.placeholder}
                    </Stack>
                  )}
                </Droppable>
              </DragDropContext>
              <Stack direction="column" m={1.5}>
                <Stack bgcolor={theme.palette.grey[900]} borderRadius={0.5}>
                  <Stack direction="row" justifyContent="space-between" p={1} px={2} alignItems="center">
                    <Typography fontSize={15} color={theme.palette.grey[400]} fontWeight={500}>
                      {t(`trainingWorkout.type.${trainingWorkoutGroup.type}`)}
                      <IconButton color="primary" onClick={() => toggleUpdateTypeGroupVisibility(trainingWorkoutGroup)}>
                        <Iconify icon={'eva:edit-fill'} />
                      </IconButton>
                    </Typography>
                    <Stack direction="row">
                      <IconButton color="default" onClick={toggleEditFormVisibility}>
                        <VisibilityOff></VisibilityOff>
                      </IconButton>
                    </Stack>
                  </Stack>
                  <Divider/>
                  {!isEditFormVisible
                    ? <Summary onPress={toggleEditFormVisibility} setup={trainingWorkoutGroup.setup} />
                    : <SetupFormGroup
                      trainingWorkoutGroupId={trainingWorkoutGroup.id}
                      setup={setupGroup}
                      workoutType={trainingWorkoutGroup.trainingsWorkouts?.[0].workout.type}
                      onClose={toggleEditFormVisibility}
                    />
                  }
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      )}
    </Draggable>
  )
}
